import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PaymentMethod, AccountType } from "@/types/payment";
import { ArrowLeft, Loader2, Building, CreditCard, AtSign, Wallet, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { safeSelect, safeInsert } from "@/utils/supabaseHelpers";

const PaymentMethodsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<Partial<PaymentMethod>>({});
  const [progress, setProgress] = useState(0);
  const [activeTab, setActiveTab] = useState<"paypay" | "bank">("paypay");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    calculateProgress();
  }, [paymentMethod]);

  const calculateProgress = () => {
    let completed = 0;
    let total = 6; // bank name, branch name, account type, account number, account holder name, paypay username
    
    if (paymentMethod.bank_name) completed += 1;
    if (paymentMethod.branch_name) completed += 1;
    if (paymentMethod.account_type) completed += 1;
    if (paymentMethod.account_number) completed += 1;
    if (paymentMethod.account_holder_name) completed += 1;
    if (paymentMethod.paypay_username) completed += 1;

    const percentage = total > 0 ? (completed / total) * 100 : 0;
    setProgress(percentage);
  };

  const fetchPaymentMethods = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('profile_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      if (data) {
        // For now, we need to handle the old database structure
        // The data from the database only has paypay_qr_url, not paypay_username yet
        let paypayValue = null;
        if (data.paypay_qr_url) {
          // Only use paypay_qr_url if it doesn't look like a storage URL
          // This prevents the QR URL from being displayed as a username
          paypayValue = !data.paypay_qr_url.includes('supabase.co/storage') ? data.paypay_qr_url : null;
        }
        
        // Map the database fields to our PaymentMethod type with the new structure
        setPaymentMethod({
          id: data.id,
          profile_id: data.profile_id,
          paypay_username: paypayValue,
          bank_name: data.bank_name,
          branch_name: data.branch_name,
          account_type: data.account_type,
          account_number: data.account_number,
          account_holder_name: data.account_holder_name,
          is_preferred: data.is_preferred
        });
      }
    } catch (error: any) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const handleInputChange = (field: keyof PaymentMethod, value: string | AccountType | boolean | null) => {
    setPaymentMethod(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isBankDetailsCompleted = () => {
    return !!paymentMethod.bank_name && 
           !!paymentMethod.branch_name && 
           !!paymentMethod.account_type && 
           !!paymentMethod.account_number && 
           !!paymentMethod.account_holder_name;
  };

  const isPaypayDetailsCompleted = () => {
    return !!paymentMethod.paypay_username;
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      console.log("Saving payment method with is_preferred:", paymentMethod.is_preferred);

      // First check if the user already has a payment method
      const { data: existingMethod } = await supabase
        .from('payment_methods')
        .select('id')
        .eq('profile_id', user.id)
        .maybeSingle();

      let result;
      if (existingMethod?.id) {
        // If there's an existing method, update it
        console.log("Updating existing payment method:", existingMethod.id);
        result = await supabase
          .from('payment_methods')
          .update({
            paypay_username: paymentMethod.paypay_username,
            bank_name: paymentMethod.bank_name,
            branch_name: paymentMethod.branch_name,
            account_type: paymentMethod.account_type,
            account_number: paymentMethod.account_number,
            account_holder_name: paymentMethod.account_holder_name,
            is_preferred: paymentMethod.is_preferred
          })
          .eq('id', existingMethod.id);
      } else {
        // If no existing method, insert a new one
        console.log("Creating new payment method");
        result = await supabase
          .from('payment_methods')
          .insert({
            profile_id: user.id,
            paypay_username: paymentMethod.paypay_username,
            bank_name: paymentMethod.bank_name,
            branch_name: paymentMethod.branch_name,
            account_type: paymentMethod.account_type,
            account_number: paymentMethod.account_number,
            account_holder_name: paymentMethod.account_holder_name,
            is_preferred: paymentMethod.is_preferred
          });
      }

      if (result.error) throw result.error;

      console.log("Payment method saved successfully with is_preferred:", paymentMethod.is_preferred);

      toast({
        title: t('profile.updateProfileSuccess'),
        description: t('payments.methods') + " " + t('common.success'),
      });

      // Check which payment methods are completed
      const bankCompleted = isBankDetailsCompleted();
      const paypayCompleted = isPaypayDetailsCompleted();

      // Determine where to go next
      if (bankCompleted && paypayCompleted) {
        // If both are complete, navigate away
        navigate('/');
      } else if (paypayCompleted && !bankCompleted && activeTab === "paypay") {
        // If paypay is complete but bank isn't, switch to bank tab
        setActiveTab("bank");
        toast({
          title: t('payments.paypayComplete'),
          description: t('payments.setupBankNext'),
        });
      } else if (bankCompleted && !paypayCompleted && activeTab === "bank") {
        // If bank is complete but paypay isn't, switch to paypay tab
        setActiveTab("paypay");
        toast({
          title: t('payments.bankDetailsComplete'),
          description: t('payments.setupPaypayNext'),
        });
      } else {
        // Stay on current tab if neither is complete
        toast({
          title: t('common.saved'),
          description: t('payments.continueSetup'),
        });
      }
    } catch (error: any) {
      console.error('Error updating payment methods:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    // Try to go back, but if we can't (no history), go to home
    try {
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1);
      } else {
        // No history or at the start, go to home
        navigate('/app');
      }
    } catch {
      // Fallback to home if there's any error
      navigate('/app');
    }
  };

  const handleSkip = () => {
    navigate('/');
  };

  return (
    <div className="container max-w-2xl mx-auto p-4 space-y-4">
      <Button
        variant="ghost"
        onClick={handleBack}
        className="mb-2"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {t('common.back')}
      </Button>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Wallet className="h-6 w-6 text-primary" />
          <div className="text-2xl font-bold">{t('payments.methods')}</div>
        </div>
      </div>
      
      <div className="mb-6">
        <Progress value={progress} className="h-2 bg-gray-100" />
        <div className="text-xs text-right mt-1 text-muted-foreground">
          {Math.round(progress)}% {t('common.completed')}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "paypay" | "bank")} className="w-full mb-6">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="paypay" className="py-3">
            <CreditCard className="mr-2 h-4 w-4" />
            PayPay
          </TabsTrigger>
          <TabsTrigger value="bank" className="py-3">
            <Building className="mr-2 h-4 w-4" />
            {t('payments.bank')}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="paypay">
          <Card className="mb-4 border-[#ff66c4]/20 hover:shadow-md transition-all">
            <CardHeader className="flex flex-row items-center space-y-0 gap-4 pb-2">
              <div className="bg-[#FFDEE2] p-3 rounded-full">
                <CreditCard className="h-7 w-7 text-[#ff66c4]" />
              </div>
              <div>
                <CardTitle>{t('payments.paypayDetails')}</CardTitle>
                <CardDescription>{t('payments.paypayHint')}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="paypay_username">{t('payments.paypayUsername')}</Label>
                <div className="relative">
                  <Input
                    id="paypay_username"
                    placeholder={t('payments.paypayExample')}
                    value={paymentMethod.paypay_username || ''}
                    onChange={(e) => handleInputChange('paypay_username', e.target.value)}
                    className="pl-10 border-[#ff66c4]/20 focus-visible:ring-[#ff66c4]/30"
                  />
                  <AtSign className="absolute left-3 top-2.5 h-5 w-5 text-[#ff66c4]/60" />
                </div>
                <p className="text-xs text-gray-500 mt-2 ml-1">
                  {t('payments.paypayHint')}
                </p>
              </div>

              {/* Preferred payment method checkbox */}
              <div className="flex items-center space-x-2 mt-6">
                <Checkbox 
                  id="is_preferred_paypay" 
                  checked={paymentMethod.is_preferred || false}
                  onCheckedChange={(checked) => handleInputChange('is_preferred', checked === true)}
                  className="data-[state=checked]:bg-[#ff66c4] data-[state=checked]:border-[#ff66c4]"
                />
                <label
                  htmlFor="is_preferred_paypay"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1"
                >
                  <Star className="h-4 w-4 text-[#ff66c4]" />
                  {t('payments.preferredMethod')}
                </label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="bank">
          <Card className="mb-4 border-[#5770fd]/20 hover:shadow-md transition-all">
            <CardHeader className="flex flex-row items-center space-y-0 gap-4 pb-2">
              <div className="bg-[#e5deff] p-3 rounded-full">
                <Building className="h-7 w-7 text-[#5770fd]" />
              </div>
              <div>
                <CardTitle>{t('payments.bankDetails')}</CardTitle>
                <CardDescription>{t('payments.bankDetailsDescription')}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="bank_name">{t('payments.bankName')}</Label>
                  <Input
                    id="bank_name"
                    placeholder={t('payments.bankNamePlaceholder')}
                    value={paymentMethod.bank_name || ''}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    className="border-[#5770fd]/20 focus-visible:ring-[#5770fd]/30"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="branch_name">{t('payments.branchName')}</Label>
                  <Input
                    id="branch_name"
                    placeholder={t('payments.branchNamePlaceholder')}
                    value={paymentMethod.branch_name || ''}
                    onChange={(e) => handleInputChange('branch_name', e.target.value)}
                    className="border-[#5770fd]/20 focus-visible:ring-[#5770fd]/30"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="account_type">{t('payments.accountType')}</Label>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant={paymentMethod.account_type === "普通預金" ? "default" : "outline"}
                    onClick={() => handleInputChange('account_type', "普通預金")}
                    className="flex-1 border-[#5770fd]/20"
                  >
                    {t('payments.savings')}
                  </Button>
                  <Button 
                    type="button" 
                    variant={paymentMethod.account_type === "当座預金" ? "default" : "outline"}
                    onClick={() => handleInputChange('account_type', "當座預金")}
                    className="flex-1 border-[#5770fd]/20"
                  >
                    {t('payments.checking')}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="account_number">{t('payments.accountNumber')}</Label>
                  <Input
                    id="account_number"
                    placeholder={t('payments.accountNumberPlaceholder')}
                    value={paymentMethod.account_number || ''}
                    onChange={(e) => handleInputChange('account_number', e.target.value)}
                    className="border-[#5770fd]/20 focus-visible:ring-[#5770fd]/30"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="account_holder_name">{t('payments.accountHolder')}</Label>
                  <Input
                    id="account_holder_name"
                    placeholder={t('payments.accountHolderPlaceholder')}
                    value={paymentMethod.account_holder_name || ''}
                    onChange={(e) => handleInputChange('account_holder_name', e.target.value)}
                    className="border-[#5770fd]/20 focus-visible:ring-[#5770fd]/30"
                  />
                </div>
              </div>

              {/* Preferred payment method checkbox */}
              <div className="flex items-center space-x-2 mt-6">
                <Checkbox 
                  id="is_preferred_bank" 
                  checked={paymentMethod.is_preferred || false}
                  onCheckedChange={(checked) => handleInputChange('is_preferred', checked === true)}
                  className="data-[state=checked]:bg-[#5770fd] data-[state=checked]:border-[#5770fd]"
                />
                <label
                  htmlFor="is_preferred_bank"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1"
                >
                  <Star className="h-4 w-4 text-[#5770fd]" />
                  {t('payments.preferredMethod')}
                </label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex gap-3 mt-6">
        <Button 
          variant="outline" 
          onClick={handleSkip} 
          className="flex-1 border-[#5770fd]/20"
        >
          {t('common.skip')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          className="flex-1" 
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
};

export default PaymentMethodsPage;
